// AppRoutes.tsx
import { ThemedLayout } from "@refinedev/mui";
import { Navigate, Outlet, Route, Routes } from "react-router";
import React, { useState } from "react";
import { ROUTES } from "./RouteConstant";
import { GlobalStyles, useTheme } from "@mui/material";
import { getMeterTableV2Styles } from "../styles/pages/meters/MeterTableV2.styles";
import { AuthProvider } from "../contexts/auth/AuthContext";
import TransactionsTab from "../pages/finance/TransactionsTab";
const TariffTab = React.lazy(() => import("../pages/operations/Tariffs/TariffTab"));
const NigeriaSmsDashboard = React.lazy(() => import("../pages/dashboard/NigeriaSmsDashboard"));
const FmsDashboard = React.lazy(() => import("../pages/fms/FmsDashboard"));

const FmsLayout = React.lazy(() => import("../pages/fms/FmsLayout"));
// FMS Admin Module Components
const AdminFmsDashboard = React.lazy(() => import("../pages/fms/AdminFmsDashboard"));
const AdminFmsAse = React.lazy(() => import("../pages/fms/AdminFmsAse"));
const AdminFmsTaskManagement = React.lazy(() => import("../pages/fms/AdminFmsTaskManagement"));
const AdminFmsTasks = React.lazy(() => import("../pages/fms/AdminFmsTasks"));
const AdminFmsPoleManagement = React.lazy(() => import("../pages/fms/AdminFmsPoleManagement"));
const AdminFmsTravelManagement = React.lazy(() => import("../pages/fms/AdminFmsTravelManagement"));
const AdminFmsInventoryManagement = React.lazy(() => import("../pages/fms/AdminFmsInventoryManagement"));
const AdminFmsDragDownConfiguration = React.lazy(() => import("../pages/fms/AdminFmsDragDownConfiguration"));
const AdminFmsNotifications = React.lazy(() => import("../pages/fms/AdminFmsNotifications"));
const AdminFmsExpense = React.lazy(() => import("../pages/fms/AdminFmsExpense"));
const AdminFmsEmployeeAdvanceLimits = React.lazy(() => import("../pages/fms/AdminFmsEmployeeAdvanceLimits"));
const UgxDashboard = React.lazy(() => import("../pages/ugx/UgxDashboard"));
const AuthWrapper = React.lazy(() => import("./AuthWrapper"));
const ProtectedRoute = React.lazy(() => import("./ProtectedRoute"));

// Lazy-load non-critical components
const NeuronSideBar = React.lazy(() => import("../components/NeuronSideBar"));
const NeuronHeader = React.lazy(() => import("../components/header/NeuronHeader"));
const HomePage = React.lazy(() => import("../pages/HomePage"));
const Meters = React.lazy(() => import("../pages/operations/Meters"));
const EditMeterPage = React.lazy(
  () => import("../pages/operations/meters/EditMeterPage")
);
const NeuronAddMeter = React.lazy(
  () => import("../pages/operations/meters/NeuronAddMeter")
);
const NeuronAddMeterV1 = React.lazy(
  () => import("../pages/operations/meters/NeuronAddMeterV1")
);
const EditMeterV1 = React.lazy(
  () => import("../pages/operations/meters/EditMeterV1")
);
const ViewMeterWIPV1 = React.lazy(
  () => import("../components/meterWIPV1/ViewMeterWIPV1")
);
const RechargeTab = React.lazy(
  () => import("../pages/operations/recharge/RechargeTab")
);
const ForbiddenPage = React.lazy(() => import("../pages/403-Forbidden"));
const ErrorComponent = React.lazy(() =>
  import("@refinedev/core").then((mod) => ({ default: mod.ErrorComponent }))
);
const ManualCashPaymentsImportPage = React.lazy(
  () => import("../pages/bulkBonus/ManualCashPaymentsImportPage")
);
const ViewTransactions = React.lazy(
  () => import("../pages/finance/ViewTransactions")
);
const WIPTransactionTablePage = React.lazy(
  () => import("../pages/neuronWip/WIPTransactionTablePage")
);
const CashPaymentsList = React.lazy(
  () => import("../pages/operations/recharge/cashPayments/CashPaymentsList")
);
const ReportsTablePage = React.lazy(
  () => import("../pages/exportReports/ReportsTablePage")
);

export default function AppRoutes() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const toggleDrawer = () => setIsDrawerOpen((prev) => !prev);
  const theme = useTheme();
  const styles = getMeterTableV2Styles(theme);

  return (
    <AuthProvider>
      <GlobalStyles styles={styles.globalBody} />

      <Routes>
        {/* Public Route - Login */}
        <Route path="/" element={(<AuthWrapper />) as any} />
        
        {/* Public route for FMS modules testing */}
       

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route
            element={
              <ThemedLayout
                Header={() => <NeuronHeader onToggleSidebar={toggleDrawer} />}
                Sider={() => (
                  <NeuronSideBar
                    isDrawerOpen={isDrawerOpen}
                    toggleDrawer={toggleDrawer}
                  />
                )}
              >
                <Outlet />
              </ThemedLayout>
            }
          >
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.OPERATIONS_TARIFFS_V2} element={<TariffTab />} />
            <Route path={ROUTES.OPERATIONS_METERS} element={<Meters />} />
            <Route path={ROUTES.ADD_METER} element={<NeuronAddMeter />} />
            <Route path={ROUTES.EDIT_METER} element={<EditMeterPage />} />
            <Route path={ROUTES.ADD_METER_V1} element={<NeuronAddMeterV1 />} />
            <Route path={ROUTES.EDIT_METER_V1} element={<EditMeterV1 />} />
            <Route
              path={ROUTES.VIEW_METERWIP_V1}
              element={<ViewMeterWIPV1 />}
            />
            <Route path={ROUTES.OPERATION_RECHARGE} element={<RechargeTab />} />
            <Route
              path="/task-management"
              element={<Navigate to={ROUTES.ADMIN_FMS_TASK_MANAGEMENT} replace />}
            />
            <Route
              path={ROUTES.ADMIN_NIGERIASMSDASHBOARD}
              element={<NigeriaSmsDashboard />}
            />
            {/* FMS Admin Module Routes with Global Layout */}
            <Route element={<FmsLayout />}>
              <Route path={ROUTES.ADMIN_FMS_DASHBOARD} element={<AdminFmsDashboard />} />
              <Route path={`${ROUTES.ADMIN_FMS_ASE}/*`} element={<AdminFmsAse />} />
              <Route path={ROUTES.ADMIN_FMS_TASK_MANAGEMENT} element={<AdminFmsTaskManagement />} />
              <Route path="/admin/fms-task-management/tasks/*" element={<AdminFmsTasks />} />
              <Route path={ROUTES.ADMIN_FMS_POLE_MANAGEMENT} element={<AdminFmsPoleManagement />} />
              <Route path={ROUTES.ADMIN_FMS_TRAVEL_MANAGEMENT} element={<AdminFmsTravelManagement />} />
              <Route path={ROUTES.ADMIN_FMS_INVENTORY_MANAGEMENT} element={<AdminFmsInventoryManagement />} />
              <Route path={ROUTES.ADMIN_FMS_DRAG_DOWN_CONFIGURATION} element={<AdminFmsDragDownConfiguration />} />
              <Route path={ROUTES.ADMIN_FMS_NOTIFICATIONS} element={<AdminFmsNotifications />} />
              <Route path={`${ROUTES.ADMIN_FMS_EXPENSE}/*`} element={<AdminFmsExpense />} />
              <Route path={`${ROUTES.ADMIN_FMS_EMPLOYEE_ADVANCE_LIMITS}/*`} element={<AdminFmsEmployeeAdvanceLimits />} />
            </Route>
            <Route path={ROUTES.FORBIDDEN} element={<ForbiddenPage />} />
            <Route path={ROUTES.MANUAL_CASH_PAYMENTS_IMPORT} element={<ManualCashPaymentsImportPage />} />
            <Route path={ROUTES.TRANSACTIONS} element={<TransactionsTab />} />
            <Route path={ROUTES.VIEW_TRANSACTIONS} element={<ViewTransactions />} />
            <Route path={ROUTES.CASH_PAYMENTS} element={<CashPaymentsList />} />
            <Route path={ROUTES.WIP_TRANSACTIONS} element={<WIPTransactionTablePage />} />
            <Route path={ROUTES.REPORTS_TABLE} element={<ReportsTablePage />} />
            {/* MFE FMS Modules Showcase */}
          
            {/* MFE FMS Routes - wildcard to handle all sub-routes */}
            <Route path={`${ROUTES.FMS}/*`} element={<FmsDashboard />} />
            {/* MFE Ugx Routes - wildcard to handle all sub-routes */}
            <Route path={`${ROUTES.Ugx}/*`} element={<UgxDashboard />} />
            <Route path="*" element={<ErrorComponent />} />
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}
